// v10/src/components/admin/inventory/modals/CompatibilityModal.js
//
// Modal "Compatibilidades" del Stock de repuestos (A8) — Tarea 2.
// Toggle "Producto universal" + ABM de compatibilidades.
//
// Endpoints:
//   POST   /api/inventory-items/:id/compatibilities
//   DELETE /api/inventory-items/:id/compatibilities/:compatId
//
// Carga inicial: GET /api/inventory-items/:id (ya devuelve compatibilities[]).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader, Plus, Trash2, Plane, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';

const CompatibilityModal = ({ isOpen, item, onClose, onSuccess, darkMode }) => {
  const { t } = useTranslation('admin');

  const [list, setList] = useState([]); // [{ id, aircraft_manufacturer, aircraft_model_pattern }]
  const [loadingList, setLoadingList] = useState(false);
  const [universal, setUniversal] = useState(false);
  const [confirmUniversal, setConfirmUniversal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addMfg, setAddMfg] = useState('');
  const [addModel, setAddModel] = useState('');
  const [addError, setAddError] = useState('');

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  // Reset al abrir
  useEffect(() => {
    if (!isOpen || !item?.id) return;
    setError('');
    setSuccess('');
    setConfirmUniversal(false);
    setAddMfg('');
    setAddModel('');
    setAddError('');
    setLoadingList(true);
    inventoryService.getById(item.id)
      .then((res) => {
        if (!isMounted.current) return;
        const data = res?.data?.data || {};
        const compats = Array.isArray(data.compatibilities) ? data.compatibilities : [];
        setList(compats);
        setUniversal(compats.length === 0);
      })
      .catch(() => {
        if (!isMounted.current) return;
        setError(t('inventory_modal_detail_load_error'));
      })
      .finally(() => {
        if (isMounted.current) setLoadingList(false);
      });
  }, [isOpen, item?.id, t]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, busy]);

  // Banner success auto-dismiss
  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => {
      if (isMounted.current) setSuccess('');
    }, 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const isDuplicate = useMemo(() => {
    const m = (addMfg || '').trim().toLowerCase();
    const mp = (addModel || '').trim().toLowerCase();
    if (!m || !mp) return false;
    return list.some(
      (c) => c.aircraft_manufacturer.toLowerCase() === m
        && c.aircraft_model_pattern.toLowerCase() === mp,
    );
  }, [addMfg, addModel, list]);

  const handleAdd = async () => {
    if (busy) return;
    const m = addMfg.trim();
    const mp = addModel.trim();
    if (!m || !mp) {
      setAddError(t('inventory_modal_compatibilities_validation_required'));
      return;
    }
    if (isDuplicate) {
      setAddError(t('inventory_modal_compatibilities_validation_duplicate'));
      return;
    }
    setAddError('');
    setBusy(true);
    setError('');
    try {
      const res = await inventoryService.addCompatibility(item.id, {
        aircraft_manufacturer: m,
        aircraft_model_pattern: mp,
      });
      if (!isMounted.current) return;
      const newRow = res?.data?.data;
      if (newRow) setList((arr) => [...arr, newRow]);
      setAddMfg('');
      setAddModel('');
      setUniversal(false);
      setSuccess(t('inventory_modal_compatibilities_success_added'));
      onSuccess?.();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err?.response?.data?.message || t('inventory_modal_network_error'));
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const handleRemove = async (compatId) => {
    if (busy || !item?.id || !compatId) return;
    setBusy(true);
    setError('');
    try {
      await inventoryService.removeCompatibility(item.id, compatId);
      if (!isMounted.current) return;
      setList((arr) => arr.filter((c) => c.id !== compatId));
      setSuccess(t('inventory_modal_compatibilities_success_removed'));
      onSuccess?.();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err?.response?.data?.message || t('inventory_modal_network_error'));
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const handleToggleUniversal = async (next) => {
    if (busy) return;
    if (next) {
      // Si hay compats, pedir confirmación primero
      if (list.length > 0 && !confirmUniversal) {
        setConfirmUniversal(true);
        return;
      }
      setBusy(true);
      setError('');
      try {
        await Promise.all(
          list.map((c) => inventoryService.removeCompatibility(item.id, c.id)),
        );
        if (!isMounted.current) return;
        setList([]);
        setUniversal(true);
        setConfirmUniversal(false);
        setSuccess(t('inventory_modal_compatibilities_success_universal'));
        onSuccess?.();
      } catch (err) {
        if (!isMounted.current) return;
        setError(err?.response?.data?.message || t('inventory_modal_network_error'));
      } finally {
        if (isMounted.current) setBusy(false);
      }
    } else {
      // Si era universal, simplemente bajamos el flag (no modifica nada hasta agregar)
      setUniversal(false);
      setConfirmUniversal(false);
    }
  };

  if (!isOpen || !item) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 110,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={busy ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 600, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(79,70,229,0.3)',
            }}>
              <Plane style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('inventory_modal_compatibilities_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('inventory_modal_compatibilities_subtitle', { name: item.name })}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={busy}
            aria-label={t('inventory_modal_close')}
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <AnimatePresence>
          {success && (
            <motion.div
              key="compat-success"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', borderRadius: 12, marginBottom: 14,
                background: 'rgba(22,163,74,0.10)',
                border: '1px solid rgba(22,163,74,0.30)',
                color: '#16a34a',
              }}
            >
              <CheckCircle2 style={{ width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 700 }}>{success}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toggle universal */}
        <div style={{ marginBottom: 14 }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 10, cursor: busy ? 'not-allowed' : 'pointer',
            padding: '10px 14px', borderRadius: 12,
            background: universal
              ? (darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)')
              : (darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9'),
            border: universal
              ? '1px solid rgba(99,102,241,0.30)'
              : (darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'),
            color: universal ? '#6366f1' : fgPrimary,
            fontSize: 13, fontWeight: 700,
          }}>
            <input
              type="checkbox" checked={universal}
              disabled={busy || loadingList}
              onChange={(e) => handleToggleUniversal(e.target.checked)}
            />
            {t('inventory_modal_compatibilities_universal_toggle')}
          </label>
          {universal && list.length === 0 && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
              {t('inventory_modal_compatibilities_universal_hint')}
            </p>
          )}
        </div>

        {confirmUniversal && (
          <div style={{
            padding: 14, borderRadius: 12, marginBottom: 14,
            background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)',
          }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#d97706' }}>
              {t('inventory_modal_compatibilities_universal_warning', { count: list.length })}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <motion.button
                type="button"
                onClick={() => handleToggleUniversal(true)}
                disabled={busy}
                whileHover={!busy ? { scale: 1.02 } : {}}
                whileTap={!busy ? { scale: 0.97 } : {}}
                style={{
                  padding: '8px 14px', borderRadius: 10, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  background: 'linear-gradient(135deg,#d97706,#f59e0b)',
                  color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
                {busy && <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />}
                {t('inventory_modal_compatibilities_universal_warning_yes')}
              </motion.button>
              <button
                type="button"
                onClick={() => setConfirmUniversal(false)}
                disabled={busy}
                style={{
                  padding: '8px 14px', borderRadius: 10,
                  border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                  background: 'transparent',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 13, color: fgMuted, fontFamily: 'inherit',
                }}
              >
                {t('inventory_modal_compatibilities_universal_warning_no')}
              </button>
            </div>
          </div>
        )}

        {/* Lista actual */}
        <div style={{ marginBottom: 16 }}>
          <p style={labelStyle}>{t('inventory_modal_compatibilities_list_label')}</p>
          {loadingList ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: fgMuted, fontSize: 13, padding: '12px 0' }}>
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
              {t('inventory_modal_detail_loading')}
            </div>
          ) : list.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: fgFaint }}>
              {t('inventory_modal_compatibilities_list_empty')}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {list.map((c) => (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', borderRadius: 12,
                  background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                  border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                }}>
                  <Plane style={{ width: 14, height: 14, color: '#6366f1' }} />
                  <p style={{ margin: 0, flex: 1, fontSize: 13, color: fgPrimary }}>
                    <strong>{c.aircraft_manufacturer}</strong> · {c.aircraft_model_pattern}
                  </p>
                  <button
                    type="button" onClick={() => handleRemove(c.id)} disabled={busy}
                    aria-label={t('inventory_modal_compatibilities_remove_action')}
                    style={{
                      padding: 6, borderRadius: 8, border: 'none',
                      background: 'transparent', color: '#ef4444',
                      cursor: busy ? 'not-allowed' : 'pointer', display: 'flex',
                    }}
                  >
                    <Trash2 style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Agregar nueva */}
        <div style={{ marginBottom: 12 }}>
          <p style={labelStyle}>{t('inventory_modal_compatibilities_add_label')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
            <input
              type="text" maxLength={120} style={inputStyle}
              value={addMfg}
              onChange={(e) => { setAddMfg(e.target.value); setAddError(''); }}
              placeholder={t('inventory_modal_compatibilities_manufacturer_placeholder')}
            />
            <input
              type="text" maxLength={120} style={inputStyle}
              value={addModel}
              onChange={(e) => { setAddModel(e.target.value); setAddError(''); }}
              placeholder={t('inventory_modal_compatibilities_model_placeholder')}
            />
            <motion.button
              type="button" onClick={handleAdd} disabled={busy}
              whileHover={!busy ? { scale: 1.02 } : {}}
              whileTap={!busy ? { scale: 0.97 } : {}}
              style={{
                padding: '10px 14px', borderRadius: 12, border: 'none',
                cursor: busy ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {busy
                ? <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                : <Plus style={{ width: 14, height: 14 }} />
              }
              {t('inventory_modal_compatibilities_add_action')}
            </motion.button>
          </div>
          {addError && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>• {addError}</p>
          )}
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: 12,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            marginBottom: 12,
          }}>
            <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button" onClick={onClose} disabled={busy}
            style={{
              padding: '13px 18px', borderRadius: 14,
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
              background: 'transparent',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 14, color: fgMuted, fontFamily: 'inherit',
            }}
          >
            {t('inventory_modal_compatibilities_action_close')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CompatibilityModal;
